"use client";

import type { CSSProperties } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import {
    Autoplay,
    Pagination,
    Navigation,
    FreeMode,
    Mousewheel,
} from "swiper/modules";
import { getStrapiMedia } from "@/utils/api-helpers";
import BottomCurves from "../curves/bottomCurves";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

type ServiceSlide = {
    id: number;
    title?: string;
    description?: string;
    image?: {
        url?: string;
        alternativeText?: string | null;
    } | null;
};

type ServicesSliderProps = {
    data: {
        id?: number;
        sliderTitle?: string;
        hasGradient?: boolean;
        waveDirection?: string | null;
        slides?: ServiceSlide[];
        waveBgColor?: {
            waveBgColor?: string | null;
        } | null;
    };
};

export default function ServicesSlider({ data }: ServicesSliderProps) {
    const paginationClass = `services-slider-progress-${data.id || "default"}`;

    return (
        <section
            className={`relative overflow-hidden pb-[10vw] md:pb-[12vw] lg:px-[10vw] ${
                data.hasGradient ? "bg-rainbow" : "bg-lightGrey"
            }`}
        >
            <div className="max-w-[93rem] mx-auto">
                {data.sliderTitle && (
                    <h3 className="text-purple font-bold text-xl sm:text-2xl md:text-3xl mb-8">
                        {data.sliderTitle}
                    </h3>
                )}

                <div>
                    <Swiper
                        slidesPerView={1.2}
                        spaceBetween={16}
                        centeredSlides={false}
                        grabCursor={false}
                        freeMode={{
                            enabled: true,
                            sticky: false,
                            momentumBounce: true,
                            momentumRatio: 0.8,
                        }}
                        breakpoints={{
                            480: {
                                slidesPerView: 1.5,
                                spaceBetween: 16,
                            },
                            640: {
                                slidesPerView: 2.2,
                                spaceBetween: 20,
                            },
                            768: {
                                slidesPerView: 2.5,
                                spaceBetween: 20,
                            },
                            1024: {
                                slidesPerView: 3.2,
                                spaceBetween: 24,
                            },
                            1280: {
                                slidesPerView: 4,
                                spaceBetween: 24,
                            },
                        }}
                        pagination={{
                            type: "progressbar",
                            el: `.${paginationClass}`,
                        }}
                        mousewheel={{
                            forceToAxis: true,
                        }}
                        modules={[
                            Autoplay,
                            Pagination,
                            Navigation,
                            FreeMode,
                            Mousewheel,
                        ]}
                        className="services-slider !overflow-visible"
                    >
                        {data.slides?.map((slide) => (
                            <SwiperSlide
                                key={slide.id}
                                className="!bg-transparent"
                            >
                                <div className="group bg-gradient-to-b from-[#8DFFFA] to-[#37DED7] rounded-3xl p-6 md:p-8 h-[320px] md:h-[350px] flex flex-col items-center justify-center text-center transition-all duration-300 hover:shadow-lg cursor-pointer">
                                    {slide.image?.url && (
                                        <div className="mb-6 transition-all duration-500 ease-in-out group-hover:opacity-0 group-hover:-translate-y-10 group-hover:scale-75 group-hover:h-0 group-hover:mb-0">
                                            <Image
                                                src={getStrapiMedia(
                                                    slide.image.url,
                                                )}
                                                width={100}
                                                height={100}
                                                alt={
                                                    slide.image
                                                        .alternativeText ||
                                                    slide.title ||
                                                    ""
                                                }
                                                className="m-auto h-auto object-contain scale-100 md:scale-100 lg:scale-[140%] imageChild"
                                            />
                                        </div>
                                    )}

                                    <h4 className="text-purple font-bold text-lg md:text-xl leading-tight transition-all duration-200 ease-in-out group-hover:opacity-0 group-hover:translate-y-8 group-hover:scale-75 group-hover:h-0 group-hover:mb-0">
                                        {slide.title}
                                    </h4>

                                    <p className="text-purple text-sm leading-relaxed md:text-base md:leading-snug mt-4 max-h-0 overflow-hidden opacity-0 translate-y-8 transition-all duration-500 ease-in-out group-hover:max-h-[15rem] group-hover:translate-y-0 group-hover:opacity-100 md:group-hover:max-h-[16.5rem]">
                                        {slide.description}
                                    </p>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <div
                        className={`${paginationClass} !relative !top-auto !left-auto !bottom-auto !w-auto h-1 bg-gray-50 rounded-full mt-8 mr-8 md:mr-10 lg:mr-24 overflow-hidden`}
                        style={
                            {
                                "--swiper-pagination-color": "#ffffff",
                            } as CSSProperties
                        }
                    >
                        <div className="swiper-pagination-progressbar-fill bg-gray-50 h-full rounded-full"></div>
                    </div>
                </div>
            </div>

            <BottomCurves
                color={
                    data.waveBgColor?.waveBgColor ||
                    (data.hasGradient ? "light grey" : "rainbow purple to blue")
                }
                direction={data.waveDirection || "left"}
                className="z-10"
            />
        </section>
    );
}
